import { observer } from "mobx-react-lite";
import * as React from "react";
import { useCallback, useRef } from "react";
import { minColWidth } from "src/components/ReactiveTable/constants";
import { RtCtrlResizable } from "src/components/ReactiveTable/ctrl";
import { RtColumnDef } from "src/components/ReactiveTable/RtColumn";

import * as styles from "./RtColumnResizerOptimized.module.less";

type PropsRtColumnResizer = {
  column: RtColumnDef;
  store: RtCtrlResizable;
  container: HTMLDivElement;
};

/**
 * Оптимизированный Column Resizer
 *
 * Оптимизации:
 * 1. Mouse events вместо HTML5 Drag API (более надежно и плавно)
 * 2. Прямая манипуляция DOM во время ресайза (без React/MobX updates)
 * 3. requestAnimationFrame для плавности 60 FPS
 * 4. Обновление store только на mouseup (1 MobX update вместо сотен)
 * 5. Только useRef (без useState) - 0 ре-рендеров во время ресайза
 * 6. Scoped querySelectorAll к текущей таблице
 * 7. Global mousemove/mouseup для работы вне границ элемента
 */
export const RtColumnResizerOptimized: React.FC<PropsRtColumnResizer> =
  observer((props) => {
    const { column, store, container } = props;

    // Вычисляем minWidth
    const colMinWidth =
      typeof column.minWidth === "number" ? column.minWidth : undefined;
    const minWidth = colMinWidth ?? minColWidth;

    // Состояние для ресайза (используем только ref, чтобы не триггерить лишние ре-рендеры)
    const isResizingRef = useRef(false);
    const handleRef = useRef<HTMLDivElement | null>(null);
    const resizeStateRef = useRef<{
      startX: number;
      startWidth: number;
      animationFrameId: number | null;
    }>({
      startX: 0,
      startWidth: 0,
      animationFrameId: null,
    });

    // Прямая манипуляция DOM (без триггера MobX)
    const updateColumnWidthDOM = useCallback(
      (width: number) => {
        if (!container) return;

        // Обновляем ширину напрямую через DOM
        container.style.width = `${width}px`;
        container.style.minWidth = `${width}px`;
        container.style.maxWidth = `${width}px`;

        // Также обновляем все ячейки этой колонки в текущей таблице
        // Используем scope к родительскому table container, чтобы не затронуть другие таблицы
        const tableContainer = container.closest("[data-table-container]");
        if (!tableContainer) return;

        const columnKey = column.key;
        const cells = tableContainer.querySelectorAll(
          `[data-column-key="${columnKey}"]`,
        );

        cells.forEach((cellElement) => {
          const cell = cellElement as HTMLElement;
          cell.style.width = `${width}px`;
          cell.style.minWidth = `${width}px`;
          cell.style.maxWidth = `${width}px`;
        });
      },
      [container, column.key],
    );

    // Обработчик mousemove (будет добавлен на document)
    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        // Проверяем, что ресайз все еще активен (защита от race conditions)
        if (!isResizingRef.current) return;

        const { startX, startWidth, animationFrameId } = resizeStateRef.current;

        // Отменяем предыдущий requestAnimationFrame
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }

        // Используем requestAnimationFrame для плавности
        resizeStateRef.current.animationFrameId = requestAnimationFrame(() => {
          // Дополнительная проверка внутри RAF callback
          if (!isResizingRef.current) return;

          const deltaX = e.clientX - startX;
          const newWidth = Math.max(startWidth + deltaX, minWidth);

          // Обновляем DOM напрямую (без MobX)
          updateColumnWidthDOM(newWidth);
        });
      },
      [minWidth, updateColumnWidthDOM],
    );

    // Обработчик mouseup (будет добавлен на document)
    const handleMouseUp = useCallback(
      (e: MouseEvent) => {
        // Сразу отключаем флаг ресайза, чтобы предотвратить race conditions
        isResizingRef.current = false;

        const { startX, startWidth, animationFrameId } = resizeStateRef.current;

        // Отменяем pending animation frame
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
          resizeStateRef.current.animationFrameId = null;
        }

        // Вычисляем финальную ширину
        const deltaX = e.clientX - startX;
        const finalWidth = Math.max(startWidth + deltaX, minWidth);

        // Сохраняем ширину в пикселях (независимо от исходного формата)
        // Если исходная ширина была в процентах, она конвертируется в пиксели
        // Обновляем store (единственный раз!)
        try {
          store.changeColumnWidth(column.key, finalWidth, false);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            "[RtColumnResizer] Failed to update column width:",
            error,
          );
        }

        // Убираем классы (прямая DOM манипуляция, без ре-рендера)
        container?.classList.remove(styles.resizing);
        // Удаляем global listeners
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      },
      [column.key, minWidth, store, container, handleMouseMove],
    );

    // Обработчик mousedown на handle
    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Проверяем, что container имеет валидную ширину
        const startWidth = container?.offsetWidth || 0;
        if (startWidth === 0) {
          return;
        }

        // Сохраняем начальные параметры
        resizeStateRef.current.startX = e.clientX;
        resizeStateRef.current.startWidth = startWidth;
        isResizingRef.current = true;

        // Добавляем классы для визуального feedback (прямая DOM манипуляция, без ре-рендера)
        container?.classList.add(styles.resizing);

        // Добавляем global listeners для отслеживания движения мыши вне элемента
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      },
      [container, handleMouseMove, handleMouseUp],
    );

    // Cleanup на unmount
    React.useEffect(
      () => () => {
        // Отменяем pending animation frame
        if (resizeStateRef.current.animationFrameId !== null) {
          cancelAnimationFrame(resizeStateRef.current.animationFrameId);
        }

        // Сбрасываем флаг ресайза
        isResizingRef.current = false;

        // Убираем классы и атрибуты
        container?.classList.remove(styles.resizing);

        // Удаляем global listeners (на случай unmount во время resize)
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      },
      [container, handleMouseMove, handleMouseUp],
    );

    return (
      <div
        ref={handleRef}
        className={styles.columnResizeHandle}
        role="presentation"
        onMouseDown={handleMouseDown}
      />
    );
  });
